/**
 * kolLayer3 — 视觉头像分析（最贵但最精准的兜底层）
 *
 * 仅对 Layer 1+2 后仍 unsure / confidence < 0.5 的账号触发。
 * 用 Claude Sonnet 4.6 vision 分析头像，区分：
 *   face / anime / pfp_nft / landscape / logo / text / default
 *
 * 缓存策略：30 天（头像换得很少）。
 * 限流：每批 5 个，间隔 2 秒。
 */

const LS_KEY = 'pl_kol_layer3_v1';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 天

function safeParse(s) {
  try { return JSON.parse(s); } catch { return {}; }
}

/** 读 cache（自动过滤过期） */
export function loadLayer3Cache() {
  try {
    const saved = safeParse(localStorage.getItem(LS_KEY) || '{}');
    const now = Date.now();
    const valid = {};
    Object.entries(saved).forEach(([username, entry]) => {
      if (entry?.ts && (now - entry.ts) < TTL_MS) {
        valid[username] = entry;
      }
    });
    return valid;
  } catch { return {}; }
}

/** 批量写 cache */
export function saveLayer3Cache(entries) {
  if (!entries || typeof entries !== 'object') return;
  try {
    const saved = safeParse(localStorage.getItem(LS_KEY) || '{}');
    const now = Date.now();
    // sweep 过期
    Object.keys(saved).forEach(k => {
      if (!saved[k]?.ts || (now - saved[k].ts) >= TTL_MS) delete saved[k];
    });
    Object.entries(entries).forEach(([username, classification]) => {
      if (!username) return;
      saved[username] = { ...classification, ts: now };
    });
    localStorage.setItem(LS_KEY, JSON.stringify(saved));
  } catch {}
}

/**
 * 调 backend 端点做视觉分析（单批最多 5 个）
 * @returns {Promise<{ [username]: { type, confidence, avatarKind, reason } }>}
 */
export async function fetchLayer3Classifications(accounts, apiBase = '') {
  if (!Array.isArray(accounts) || accounts.length === 0) return {};
  const payload = accounts.slice(0, 5).map(a => ({
    username: a.username,
    avatarUrl: a.avatarUrl,
  }));
  try {
    const res = await fetch(`${apiBase}/api/kol-vision`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accounts: payload }),
    });
    if (!res.ok) return {};
    const json = await res.json();
    return json?.classifications || {};
  } catch {
    return {};
  }
}

/**
 * 合并 Layer 3 结果到 classification
 * Layer 3 仅在 Layer 1+2 后仍低信心时生效，且高置信视觉信号才会被采纳
 */
export function mergeWithLayer3(prevResult, layer3Result) {
  if (!layer3Result) return prevResult;
  // Layer 1+2 已有高信心 → 保留
  if (prevResult?.confidence >= 0.7) return prevResult;
  // Layer 3 自己也低信心 → 不强行覆盖
  if (layer3Result.confidence < 0.5) return prevResult;

  return {
    type: layer3Result.type,
    confidence: layer3Result.confidence,
    signals: { ...(prevResult?.signals || {}), aiLayer3: layer3Result.avatarKind },
    reason: layer3Result.reason || prevResult?.reason,
    refined: true,
    refinedLayer: 3, // 区分 Layer 2/3 来源
    avatarKind: layer3Result.avatarKind,
  };
}
